import { useSnackbar } from 'notistack';
import { useApi } from '../../../shared/context';
import { useMutation, useQueryClient } from 'react-query';
import { Dialog, DialogContent, DialogTitle, Grid } from '@mui/material';
import {
	CerebroDropdown,
	CerebroDropdownMenuItem,
	CerebroForm,
	CerebroInput,
	CerebroLabel,
	CerebroMaskedInput,
	CerebroPrimaryButton,
	CerebroSecondaryButton,
	CerebroText,
} from '../../../shared/components';
import { Field, Formik, FormikErrors } from 'formik';
import { FormikUtils, NumberUtils } from '../../../shared/utils';
import { CerebroApiEndpoints } from '../../../shared/utils/api';
import { QueryKeys } from '../../../shared/hooks';
import { EmptyGuid } from '../../../shared/constants';
import {
	CollateralSecurityTypeMap,
	CollateralSecurityTypes,
	FacilityAvailabilityLimitTypes,
	FacilityAvailabilityLimitTypesMap,
	TermSheetFacility,
	TermSheetFacilityCollateral,
} from '../../../shared/models';
import { MaxCurrencyLength } from '../../../shared/utils/platform';
import { getApiResponseData } from '../../../shared/utils/axios';

interface CollateralFormDialogProps {
	dataRoomGuid: string | undefined;
	termSheetGuid: string | undefined;
	revisionGuid: string | undefined;
	facility: TermSheetFacility | null;
	collateral: TermSheetFacilityCollateral | null;
	close: () => void;
}

export const CollateralFormDialog: React.FC<CollateralFormDialogProps> = ({
	dataRoomGuid,
	termSheetGuid,
	revisionGuid,
	facility,
	collateral,
	close,
}) => {
	const api = useApi();
	const snackbar = useSnackbar();
	const client = useQueryClient();

	const collateralMutation = useMutation(
		async ({ values }: CollateralFormMutationVariables) => {
			const patchValues = {
				collateralSecurityType: values.collateralSecurityType,
				availabilityLimitType: values.availabilityLimitType,
				advancedRate: NumberUtils.ParseDecimal(values.advancedRate, undefined),
				otherAvailabilityLimit: values.otherAvailabilityLimit,
			};

			const { data, error } = await getApiResponseData<TermSheetFacilityCollateral>(
				collateral?.guid == EmptyGuid
					? api.post(
							CerebroApiEndpoints.TermSheets.FacilityCollateral(
								dataRoomGuid,
								termSheetGuid,
								revisionGuid,
								facility?.guid
							),
							patchValues
					  )
					: api.patch(
							CerebroApiEndpoints.TermSheets.SpecificFacilityCollateral(
								dataRoomGuid,
								termSheetGuid,
								revisionGuid,
								facility?.guid,
								collateral?.guid
							),
							patchValues
					  )
			);

			if (error) {
				throw new Error(error.response?.data);
			}

			return data;
		},
		{
			onSuccess: () => {
				client.invalidateQueries(
					QueryKeys.TermSheets.FacilityCollateral(dataRoomGuid, termSheetGuid, revisionGuid, facility?.guid)
				);
				client.invalidateQueries(QueryKeys.TermSheets.Specific(dataRoomGuid, termSheetGuid));
				client.invalidateQueries(QueryKeys.TermSheets.Revision(dataRoomGuid, termSheetGuid, revisionGuid));
				snackbar.enqueueSnackbar('Facility collateral was successfully saved.', {
					variant: 'success',
				});
				close();
			},
			onError: (error: Error) => {
				snackbar.enqueueSnackbar(error.message, {
					variant: 'error',
				});
				close();
			},
		}
	);

	var validateForm = (values: CollateralFormValues): CollateralFormErrors => {
		const errors: CollateralFormErrors = {};

		if (!values.collateralSecurityType) {
			errors.collateralSecurityType = 'Collateral type is required.';
		}

		if (!values.availabilityLimitType) {
			errors.availabilityLimitType = 'Availability limit is required.';
		}

		if (values.availabilityLimitType == 'Custom') {
			if (!values.otherAvailabilityLimit || values.otherAvailabilityLimit.trim() == '') {
				errors.otherAvailabilityLimit = 'Availability details are required.';
			}
		} else {
			if (!values.advancedRate) {
				errors.advancedRate = 'Advanced rate is required.';
			}
		}

		return errors;
	};

	return (
		<Dialog open={collateral !== null} onClose={close} fullWidth maxWidth="md">
			<DialogTitle>Collateral</DialogTitle>
			<DialogContent>
				<Formik
					initialValues={{
						collateralSecurityType: collateral?.collateralSecurityType ?? 'Unsecured',
						availabilityLimitType: collateral?.availabilityLimitType ?? 'None',
						advancedRate: isLoadingCurrency(collateral?.availabilityLimitType)
							? NumberUtils.FormatDecimal(collateral?.advancedRate)
							: NumberUtils.FormatFloat(collateral?.advancedRate, 8),
						otherAvailabilityLimit: collateral?.otherAvailabilityLimit ?? '',
					}}
					validate={validateForm}
					onSubmit={FormikUtils.MutateOnSubmit(collateralMutation.mutate)}
				>
					{({ values, isSubmitting, errors, touched, handleSubmit, setFieldValue }) => (
						<CerebroForm onSubmit={handleSubmit}>
							<Grid item xs={11}>
								<CerebroLabel required id="collateralSecurityType">
									Type
								</CerebroLabel>
								<Field
									id="collateralSecurityType"
									as={CerebroDropdown}
									name="collateralSecurityType"
									disabled={isSubmitting}
									error={
										touched.collateralSecurityType && errors.collateralSecurityType !== undefined
									}
								>
									{Object.entries(CollateralSecurityTypeMap).map(([value, description]) => (
										<CerebroDropdownMenuItem key={value} value={value}>
											{description}
										</CerebroDropdownMenuItem>
									))}
								</Field>
								{errors.collateralSecurityType && (
									<CerebroText variant="error" sx={{ marginBottom: 4 }}>
										{errors.collateralSecurityType}
									</CerebroText>
								)}
							</Grid>
							<Grid item xs={11}>
								<CerebroLabel required id="availabilityLimitType">
									Availability Limit
								</CerebroLabel>
								<Field
									id="availabilityLimitType"
									as={CerebroDropdown}
									name="availabilityLimitType"
									disabled={isSubmitting}
									error={touched.availabilityLimitType && errors.availabilityLimitType !== undefined}
								>
									{Object.entries(FacilityAvailabilityLimitTypesMap).map(([value, description]) => (
										<CerebroDropdownMenuItem key={value} value={value}>
											{description}
										</CerebroDropdownMenuItem>
									))}
								</Field>
								{errors.availabilityLimitType && (
									<CerebroText variant="error" sx={{ marginBottom: 4 }}>
										{errors.availabilityLimitType}
									</CerebroText>
								)}
							</Grid>
							{values.availabilityLimitType == 'Custom' ? (
								<Grid item xs={11}>
									<CerebroLabel id="otherAvailabilityLimit" required>
										Availability Details
									</CerebroLabel>
									<Field
										as={CerebroInput}
										id="otherAvailabilityLimit"
										name="otherAvailabilityLimit"
										disabled={isSubmitting}
										error={
											touched.otherAvailabilityLimit &&
											errors.otherAvailabilityLimit !== undefined
										}
									/>
									{errors.otherAvailabilityLimit && (
										<CerebroText variant="error" sx={{ marginBottom: 4 }}>
											{errors.otherAvailabilityLimit}
										</CerebroText>
									)}
								</Grid>
							) : (
								<Grid item xs={11}>
									<CerebroLabel id="advancedRate" required>
										Advance Rate
									</CerebroLabel>
									{isCurrency(values) ? (
										<Field
											formatas="currency"
											as={CerebroMaskedInput}
											maxLength={MaxCurrencyLength}
											id="advancedRate"
											name="advancedRate"
											disabled={isSubmitting}
											error={touched.advancedRate && errors.advancedRate !== undefined}
										/>
									) : (
										<Field
											as={CerebroMaskedInput}
											allowDecimals={true}
											minDecimalPlaces={1}
											maxDecimalPlaces={8}
											id="advancedRate"
											name="advancedRate"
											disabled={isSubmitting}
											error={touched.advancedRate && errors.advancedRate !== undefined}
										/>
									)}
									{errors.advancedRate && (
										<CerebroText variant="error" sx={{ marginBottom: 4 }}>
											{errors.advancedRate}
										</CerebroText>
									)}
								</Grid>
							)}

							<CerebroPrimaryButton type="submit" loading={isSubmitting} sx={{ marginTop: 6 }}>
								Save
							</CerebroPrimaryButton>
							<CerebroSecondaryButton sx={{ marginTop: 6, marginLeft: 1 }} onClick={() => close()}>
								Cancel
							</CerebroSecondaryButton>
						</CerebroForm>
					)}
				</Formik>
			</DialogContent>
		</Dialog>
	);
};

type CollateralFormErrors = FormikErrors<CollateralFormValues>;
type CollateralFormMutationVariables = FormikUtils.MutateOnSubmitVariables<CollateralFormValues>;
interface CollateralFormValues {
	collateralSecurityType: CollateralSecurityTypes;
	advancedRate: string;
	availabilityLimitType: FacilityAvailabilityLimitTypes;
	otherAvailabilityLimit: string;
}

function isCurrency(value: CollateralFormValues): boolean {
	return value.availabilityLimitType == 'None' || value.availabilityLimitType == 'Limit';
}

function isLoadingCurrency(value: FacilityAvailabilityLimitTypes | undefined): boolean {
	if (!value) return true;

	return value == 'None' || value == 'Limit';
}
